import { httpClient } from 'common/services';
import { formatPhoneNumberForCalleeUpdate } from 'features/call/call-base/helpers';
import type {
  BlockCustomerRequest,
  Call,
  CloseCallRequest,
  ConnectionStateHistoryRequest,
  DenyCallRequest,
  FinalizeUnexpectedEndRequest,
  HearingInfoRequest,
  HearingInfoResponse,
  PhoneNumberCoreResolution,
  TakeCallRequest,
  UpdateCalleePhoneNumberRequest,
  AddCallPropertyRequest,
} from 'features/call/call-base/interfaces';

interface MissedCallRequestDto {
  CallId: number;
  CalleeDialString: string;
  CallPublicId: string;
  CallerDialString: string;
}

async function getCall(callId: number) {
  const route = `/call/${callId}`;
  const response = await httpClient.get<Call>(route);
  return response.data;
}

async function takeCall({ sessionId, ...rest }: TakeCallRequest) {
  const route = `/call/take/${sessionId}`;
  const response = await httpClient.put<Call>(route, rest);
  return response.data;
}

async function closeCall({ callId, keepAlive, ...rest }: CloseCallRequest) {
  const route = `/call/${callId}/close`;
  const response = await httpClient.put<void>(route, rest, { keepAlive });
  return response.data;
}

async function blockCustomer({ callId }: BlockCustomerRequest) {
  const route = `/call/${callId}/block`;
  const response = await httpClient.post<void>(route);
  return response.data;
}

async function denyCall({ callId, userStatus }: DenyCallRequest) {
  const route = `/call/${callId}/deny`;
  const response = await httpClient.put<void>(route, { userStatus });
  return response.data;
}

async function updateCall(callId: number, partialCall: Partial<Call>) {
  const route = `/call/${callId}`;
  const response = await httpClient.patch<Call>(route, partialCall);
  return response.data;
}

async function updateCalleePhoneNumber({
  callId,
  phoneNumber,
  ...rest
}: UpdateCalleePhoneNumberRequest) {
  const route = `/call/${callId}/callee-phone-number`;
  const formattedPhoneNumber = formatPhoneNumberForCalleeUpdate(phoneNumber);
  const response = await httpClient.put<Call>(route, {
    phoneNumber: formattedPhoneNumber,
    ...rest,
  });
  return response.data;
}

async function connectionStateHistory(options: ConnectionStateHistoryRequest) {
  const route = `/call/${options.callId}/connection-state-history`;
  const response = await httpClient.put<Call>(route, options);
  return response.data;
}

async function starvationReset(callId: number) {
  const route = `/call/${callId}/starvation-reset`;
  const response = await httpClient.patch<Call>(route);
  return response.data;
}

async function interpretUpdate(callId: number) {
  const route = `/call/${callId}/interpret`;
  const response = await httpClient.patch<Call>(route);
  return response.data;
}

async function lookupHearingInfoAndUpdateCall({
  callId,
  callerNumber,
  calleeNumber,
  isCallerIdBlocked,
}: HearingInfoRequest) {
  const route = `/call/${callId}/hearinginfo`;
  const response = await httpClient.put<HearingInfoResponse>(route, {
    callerNumber,
    calleeNumber,
    isCallerIdBlocked,
  });
  return response.data;
}

async function resolvePhoneNumberWithCore(
  callerNumber: string,
  calleeNumber: string
) {
  const route = `/call/resolve/${callerNumber}/${calleeNumber}`;
  const response = await httpClient.get<PhoneNumberCoreResolution>(route);
  return response.data;
}

async function missedCallAddNotification(
  callId: number,
  publicCallId: string,
  calleeDialString: string,
  callerDialString: string
) {
  const route = `/call/missed-call-add`;
  const requestBody: MissedCallRequestDto = {
    CallId: callId,
    CallPublicId: publicCallId,
    CalleeDialString: calleeDialString,
    CallerDialString: callerDialString,
  };
  const response = await httpClient.post<void>(route, requestBody);
  return response.data;
}

/**
 * Instructs the Proxy to handle the close-call logic.
 *
 * This is needed because closing a call properly
 * requires fetching the interpreting time from Relay,
 * and Mercury (client) cannot do that while it's being
 * unloaded from the browser (refreshed or closed mid-call).
 */
function finalizeUnexpectedEnd(data: FinalizeUnexpectedEndRequest) {
  httpClient.post('/call/finalize-unexpected-end', data, {
    keepAlive: true,
  });
}

async function getLastFiveCallsByInterpreterId(interpreterId: number | string) {
  const route = `/call/recent/${interpreterId}`;
  const response = await httpClient.get(route);
  return response.data;
}

async function addCallProperty({
  callId,
  propertyType,
  propertyValue,
}: AddCallPropertyRequest) {
  const route = `/call/${callId}/add-property`;
  const response = await httpClient.post<void>(route, {
    propertyType,
    propertyValue,
  });
  return response.data;
}

export const CallDaoService = {
  getCall,
  takeCall,
  closeCall,
  denyCall,
  updateCall,
  blockCustomer,
  updateCalleePhoneNumber,
  connectionStateHistory,
  starvationReset,
  interpretUpdate,
  lookupHearingInfoAndUpdateCall,
  resolvePhoneNumberWithCore,
  finalizeUnexpectedEnd,
  missedCallAddNotification,
  getLastFiveCallsByInterpreterId,
  addCallProperty,
};
